import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import * as KeySet from '@packages/utilities/key-set';
// Copy over the legacy style system style rule format.
import type {
  StyleRule as LegacyStyleRule,
  DefaultStyleConfig,
  DefaultStyleDeclaration,
  ConditionalDefaultStyleDeclaration,
  DefaultBaseStyleConfig,
  StyleBlockId,
  VariantStyleDeclaration,
} from './legacy';

export type {StyleBlockId};

export type {CssPropertyMap} from './CssPropertyMap';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
export type GUID = string;

export type CascadeLevel = 0 | 1 | 2 | 3;
export type WebflowStyleRule = LegacyStyleRule;
export type StyleSource = WebflowStyleRule;

// TODO waiting for the new StyleRule impl.
export type StyleRule = any;

export type StyleRulesByBreakpointId = Partial<
  Record<BreakpointID, Array<StyleRule>>
>;

// TODO: Add GUID as BreakpointID variant when we do custom breakpoints.
export type BreakpointID =
  | typeof BREAKPOINT_ID_XXL
  | typeof BREAKPOINT_ID_XL
  | typeof BREAKPOINT_ID_LARGE
  | typeof BREAKPOINT_ID_MAIN
  | typeof BREAKPOINT_ID_MEDIUM
  | typeof BREAKPOINT_ID_SMALL
  | typeof BREAKPOINT_ID_TINY;

export const VALID_BREAKPOINT_IDS = new Set([
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
]);

export const getBreakpointID = (str: string): null | BreakpointID => {
  // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"large" | "medium" | "small" | "main" | "xxl" | "xl" | "tiny"'.
  if (VALID_BREAKPOINT_IDS.has(str)) {
    return str as BreakpointID;
  }
  return null;
};

// TODO: Add GUID as BreakpointID variant when we do custom breakpoints.
export type BreakpointsMap<Value> = {
  xxl: Value;
  xl: Value;
  large: Value;
  main: Value;
  medium: Value;
  small: Value;
  tiny: Value;
};

export type BreakpointsConfig = Partial<
  Record<
    BreakpointID,
    | {
        maxWidth: number;
        minWidth?: number;
      }
    | {
        maxWidth?: number;
        minWidth: number;
      }
  >
>;

/**
 * If a style block contains any custom variants, a `Record` of all
 * `VariantStateKey`s (with `true` as values) must be known during render-time
 * in order to look up the appropriate `VariantData`, which is used to generate
 * the class names and/or HTML attributes necessary for the variant's styles to
 * apply to any applicable nodes.
 *
 * This class is used to express such a scenario, and the `classNameMap` field
 * of `RenderContext` has been updated to support this when required. For style
 * blocks without any associated `variantStateKeys`, a plain string can be used.
 *
 * The `variantStateKeys` field uses a `Record<VariantStateKey, true>` instead
 * of a `Set<VariantStateKey>` to facilitate storage in AWS S3, which doesn't
 * have native Set support. This approach allows for easy serialization and
 * deserialization while maintaining the set-like behavior.
 */
export type ClassNameAndVariantStateKeys = {
  name: string;
  variantStateKeys: KeySet.Type;
};

/**
 * A mapping of Style Block IDs -> class names _or_
 * `ClassNameAndVariantStateKeys` objects.
 *
 * See the inline documentation for `ClassNameAndVariantStateKeys` for details.
 */
export type ClassNameMap = {
  [key: StyleBlockId]: string | ClassNameAndVariantStateKeys;
};

/**
 * Set of all "active" variants, encoded as `VariantStateKey`s.
 */
export type ActiveVariants = KeySet.Type;

export type {
  DefaultStyleConfig,
  DefaultStyleDeclaration,
  DefaultBaseStyleConfig,
  ConditionalDefaultStyleDeclaration,
  VariantStyleDeclaration,
};
